export const GUIDES = [
  {
    slug: "getting-started-cardark",
    title: "Getting Started with CardArk: The Complete User Guide",
    description:
      "The full walkthrough — from your first scan to listing cards on eBay. Sign-in, scanning, importing spreadsheets, pricing, showcases, community features, sales tracking and integrations, all in one place.",
    category: "Getting Started",
    readTime: "10 min read",
    intro:
      "CardArk turns your trading card collection into a searchable, priced, share-ready digital binder — and now sells for you too. This guide walks through every feature so you can pick it up and use it in under 15 minutes.",
    sections: [
      {
        heading: "1. Signing in",
        paragraphs: [
          "CardArk uses Google sign-in — no passwords to remember. Click Sign in with Google in the top right, pick your Google account, and you're in. Your first 15 scan credits are already loaded, refreshed monthly on the free plan.",
          "Everything you upload stays private to your account by default. You choose what (if anything) to make public later — see section 6.",
        ],
      },
      {
        heading: "2. Adding cards — four ways",
        paragraphs: [
          "There are four ways to get cards into your binder, pick whichever fits your situation:",
          "**A. Video scan (fastest for a stack)**: Go to Scan Cards → drop a video of yourself stacking or flipping cards. The AI extracts each unique card, identifies it, and prices it. One credit per 4 cards. Best for 20+ cards at once.",
          "**B. Individual photo upload**: Same page, click 'Upload individual photos' and pick one or many. Same credit rate, higher accuracy per card because you control the framing.",
          "**C. Manual add**: On your binder, click 'Add card' — type in name, set, condition, quantity. No credit cost, no image lookup. Fastest if you already know exactly what you have.",
          "**D. CSV / Excel bulk import (new)**: Got a spreadsheet from another tracker or eBay? Scan Cards → 'Bulk import CSV / Excel'. Drop the file, we auto-detect columns (Set Name, Card Name, Condition, Card Number, Quantity, Variant, Language, Grade, Purchase Price, Notes), and pull official images + prices for every row. Costs 1 credit per 25 rows — a 900-card list costs 36 credits.",
        ],
      },
      {
        heading: "3. Reviewing after a scan",
        paragraphs: [
          "Once a scan finishes, you'll land on the Review page showing every card the AI identified with a confidence flag. Green = high confidence, amber = probable, red = needs your eyes.",
          "Click any card to open its full detail page — you can correct the name/set/number, swap the reference image from a list of official candidates, change condition, tick 'graded' with PSA/BGS/CGC/SGC grade, or delete it.",
          "Anything you don't touch is treated as confirmed and shows up in your main Dashboard.",
        ],
      },
      {
        heading: "4. Understanding card prices",
        paragraphs: [
          "Every priced card shows a value derived from real recent sales — not asking prices, not AI guesses (unless we couldn't find any sales at all).",
          "The number is a **trimmed median** of recent completed sales: we drop obvious outliers (both the £999 mislistings and the £1 scams) then take the middle value of what's left. That's the same standard used by every serious price guide.",
          "On the card detail page you can see: the current price, base near-mint value, condition multiplier applied, price trend (%), and the recent sales that made up the median. Click 'Re-scan market price' to fetch a fresh number any time.",
        ],
      },
      {
        heading: "5. Organising your binder",
        paragraphs: [
          "The Dashboard is your master view. Filter by game, collection, or search by name. Sort by value, name, newest, or trend.",
          "**Collections**: Create custom binders — 'Vintage Base Set', 'Sold on eBay', 'For trade only'. Every card can live in one collection at a time.",
          "**Checklists**: A collection can also be a set checklist — mark which cards from a set (e.g. Base Set 102/102) you already own. Missing-card view shows the gaps.",
          "**Quantities**: Instead of duplicating rows, cards have a quantity field. Each copy can even have its own condition — click 'Set per-copy conditions' on a card.",
          "**Serial numbers**: For numbered cards (e.g. Alt Arts /100), fill in the serial_number field. It shows on the card tile as a badge.",
        ],
      },
      {
        heading: "6. Showcase — sharing your binder",
        paragraphs: [
          "Every collector can enable a public **Showcase** — a browsable, view-only version of your binder anyone with the link can see. No account needed for viewers.",
          "In your binder header, click Showcase → toggle on → copy the link. Paste it in Discord, Reddit or X and it unfurls into a preview card with your top cards, total count and total value (unless you turn value display off in Settings).",
          "Individual cards can also be flexed — on a card detail page, click Share and copy the /flex/ link. Same idea, just one card.",
        ],
      },
      {
        heading: "7. Community — Explore, Chase Deck and Trades",
        paragraphs: [
          "The Explore page (Community in the nav) shows every public collector's binder. Search across all binders at once, see the value leaderboard, and browse Top Movers (cards whose price shifted the most this week).",
          "**Chase Deck**: heart any card in a public binder to add it to your personal Chase Deck (Trades page). You get an email the moment that card appears in another user's public binder — perfect for hunting a specific alt art.",
          "**Trade pings**: found a card you want? Click Ping on the owner's binder. They get notified, and if they accept, contact info is exchanged.",
          "**Rivalry alerts**: opt-in in Settings. If someone overtakes you on the value leaderboard (or vice versa), you get an email. Great for motivation.",
        ],
      },
      {
        heading: "8. Sales Ledger — tracking what you sell",
        paragraphs: [
          "When you sell a card, mark it as Sold on the card detail page — enter the sale price, and CardArk moves it into your Sales Ledger. Your binder still lets you keep the record without inflating your current value.",
          "The Sales Ledger (top nav) shows every card sold, sale price vs market value, profit or loss per sale, and lifetime totals. If you entered a Purchase Price during CSV import or on the card, profit is calculated automatically.",
          "It's a private ledger — nothing on it is public.",
        ],
      },
      {
        heading: "9. eBay integration (new)",
        paragraphs: [
          "**Connect once**: Settings → 'eBay seller account' → Connect. eBay's OAuth screen opens, click Accept, done. Your refresh token is encrypted and stored per user.",
          "**Live 'For Sale' data**: on any card detail page you'll now see the cheapest active eBay listing with seller feedback, and a benchmark hint ('9% below CardArk's estimate — competition is fierce'). Use it to price your own listings intelligently.",
          "**Bulk draft listings**: Binder → 'List on eBay' button → pick marketplace (UK/US/DE/AU/CA) → pick cards → pick your business policies (fulfillment, payment, return) → hit Create drafts. CardArk pushes unpublished drafts into your eBay Seller Hub with pre-filled title, description, condition, aspects, category and image. You review and publish yourself — CardArk never posts anything live.",
          "**First-time setup**: you'll need business policies configured in eBay Seller Hub (2-min setup at https://www.ebay.com/sh/set/business-policies). CardArk will nudge you if any are missing.",
        ],
      },
      {
        heading: "10. Credits and billing",
        paragraphs: [
          "Free plan: 15 scan credits refreshed monthly. That's ~60 cards per month at 1 credit per 4 cards.",
          "**Collector Pass** (£8.99 / month equivalent): 400 credits, valid 30 days. Roughly 1,600 cards.",
          "**Credit Top-up** (£4.99): 150 credits, no expiry.",
          "Billing runs on Stripe — no card number stored on CardArk. Buy from the Billing page or when a scan hits the credit floor.",
          "Manual card adds and CSV imports have their own separate cost (manual = free, imports = 1 credit per 25 rows).",
        ],
      },
      {
        heading: "11. Settings you should visit",
        paragraphs: [
          "Head to Settings once you're set up:",
          "**Binder name** — a public display name (e.g. 'Charizard Cave'). If you leave it blank, you show as 'Collector-XYZ' in public views.",
          "**Public binder toggle** — hide your binder from Explore entirely if you prefer.",
          "**Show collection value** — separate toggle to hide the value total on your showcase while keeping card prices visible.",
          "**Email preferences** — leaderboard rivalry, chase matches, weekly digest.",
          "**eBay seller account** — connect / disconnect.",
          "**Referral link** — invite others, both of you get 10 free credits per signup.",
        ],
      },
      {
        heading: "12. Referral programme",
        paragraphs: [
          "Every user gets a unique invite link (Settings → Invite & earn). When a new collector signs up with your link, you both get 10 free scan credits.",
          "Guards: invite must be used within 48 hours of signup, referrer must have been on CardArk for 7+ days, per-IP and per-day limits to stop abuse. Real invites work smoothly.",
        ],
      },
      {
        heading: "13. Privacy and safety",
        paragraphs: [
          "GPS and camera EXIF data is automatically stripped from every image you upload — nothing leaks your location.",
          "Public views only ever expose card photos and public fields — never your personal notes, target sale prices, matched listings or purchase history.",
          "Report a fake/abusive binder from any card's menu — it goes to the admin dashboard for review.",
          "Delete your account any time by emailing hello@hiddenforge.co.uk.",
        ],
      },
      {
        heading: "14. Getting help",
        paragraphs: [
          "This Guides section (top nav) has 12 detailed articles on scanning, pricing, grading and selling.",
          "For bugs or feature requests: hello@hiddenforge.co.uk.",
          "For account or billing issues: also hello@hiddenforge.co.uk — real human reply within 48h.",
        ],
      },
    ],
  },
  {
    slug: "scanning-best-practices",
    title: "How to Scan Trading Cards with Video: Best Practices",
    description:
      "Get the cleanest AI card scans from your stacking videos. Lighting, backgrounds, camera position, stacking pace and sleeve glare — everything that affects card recognition accuracy.",
    category: "Scanning",
    readTime: "5 min read",
    intro:
      "CardArk turns one video of you stacking cards into a fully identified, priced digital collection. The AI is good — but the quality of your video decides how good. These are the exact habits that separate a 100% clean scan from one that needs manual fixes.",
    sections: [
      {
        heading: "1. Light the cards, not the room",
        paragraphs: [
          "The single biggest factor in recognition accuracy is even, diffused light on the card face. The AI reads names, set symbols and card numbers straight off the artwork — shadows and blown-out highlights hide exactly those details.",
        ],
        list: [
          "Use daylight from a window or a desk lamp bounced off a wall — never a bare bulb directly overhead.",
          "Avoid harsh overhead lights that cast your own hand's shadow over each card as you place it.",
          "Holo and reverse-holo cards reflect light: tilt your light source slightly off-angle so the foil doesn't flare white.",
        ],
      },
      {
        heading: "2. Use a plain, contrasting background",
        paragraphs: [
          "CardArk auto-crops and straightens every card by finding its rectangular outline. A busy tablecloth or a background the same colour as the card border makes edge detection harder.",
        ],
        list: [
          "A plain dark mat for light-bordered cards, or a plain light surface for dark-bordered cards, works best.",
          "Avoid patterned surfaces, wood grain with strong lines, or other cards lying nearby in frame.",
        ],
      },
      {
        heading: "3. Lock the camera down",
        paragraphs: [
          "Film top-down with the camera fixed — propped on books, a tripod, or a phone stand. The motion detector watches for a new card settling; a wobbling camera looks like constant motion and can merge two cards into one event.",
          "Frame the stack so the card fills roughly a third to half of the frame. Too far away and text becomes unreadable; too close and your hand covers the card during placement.",
        ],
      },
      {
        heading: "4. Pace your stacking",
        paragraphs: [
          "The detector waits for the scene to go still before capturing the sharpest frame of each new card. Give it that moment.",
        ],
        list: [
          "Place a card, pull your hand fully out of frame, count one second, then place the next.",
          "Don't slide cards around after placing them — set each one down flat in one motion.",
          "Keep each card fully covering the one beneath it, so the top card is always the one being read.",
        ],
      },
      {
        heading: "5. Sleeves, toploaders and glare",
        paragraphs: [
          "Penny sleeves are usually fine. Toploaders and glossy sleeves catch light and can throw a white streak across the card name — the most common cause of a misread.",
        ],
        list: [
          "If a card is valuable enough for a toploader, consider scanning it before you seal it, or angle your light lower.",
          "Matte sleeves scan noticeably better than glossy ones.",
        ],
      },
      {
        heading: "6. Video settings that work",
        paragraphs: [
          "1080p at normal speed is the sweet spot — 4K adds upload time without improving recognition. Keep clips under a few minutes; if you have 200 cards, several shorter videos are easier to review than one marathon take. Files up to 500MB are accepted (MP4, MOV or WEBM).",
        ],
      },
      {
        heading: "When a card is missed or misread",
        paragraphs: [
          "No scan is perfect, and CardArk assumes that. Every card lands in Review with your snapshot next to the AI's matched reference image, so a wrong match is obvious at a glance.",
        ],
        list: [
          "Wrong match? Open the card and use 'Possible matches' to pick the right listing, or upload your own reference image.",
          "Misidentified? Add a hint in the 'Add context' box (e.g. 'this is the 1st Edition Jungle print') and hit Re-scan with AI.",
          "Missed entirely? Use 'Add card' in your Binder to add it manually — with or without a photo.",
        ],
      },
    ],
  },
  {
    slug: "card-condition-pricing-guide",
    title: "Trading Card Conditions Explained: From Near Mint to Damaged",
    description:
      "What Near Mint, Lightly Played, Moderately Played, Heavily Played and Damaged actually mean, how each condition affects a card's market value, and how to grade your own cards honestly.",
    category: "Pricing",
    readTime: "4 min read",
    intro:
      "Two copies of the same card can differ in value by 4x based on condition alone. Here's what each grade actually means, what to look for, and how CardArk applies condition to price.",
    sections: [
      {
        heading: "The five conditions and their price impact",
        paragraphs: [
          "Market prices are quoted for Near Mint by default. CardArk prices every card at its Near Mint baseline, then applies a multiplier when you set the condition slider:",
        ],
        list: [
          "Near Mint (NM) — 100% of market value. Pack-fresh look; at most one tiny flaw visible only under close inspection.",
          "Lightly Played (LP) — ~85%. Minor edge wear or a small scratch visible on close look; still displays beautifully.",
          "Moderately Played (MP) — ~65%. Obvious wear: whitening on edges, several scratches, light creasing possible.",
          "Heavily Played (HP) — ~45%. Major wear visible at arm's length: creases, heavy whitening, possible water damage.",
          "Damaged (DMG) — ~25%. Structural damage: tears, deep creases, writing, or missing pieces.",
        ],
      },
      {
        heading: "The four things graders look at",
        list: [
          "Centering — is the artwork evenly framed by the border? Off-centre cards grade lower even fresh from the pack.",
          "Corners — the first place wear shows. Check all four under light for whitening or softness.",
          "Edges — look along each edge for chipping or whitening, especially on dark-bordered cards.",
          "Surface — tilt the card under light to reveal scratches, print lines, dents and shuffle wear on the holo layer.",
        ],
      },
      {
        heading: "Grade honestly — especially if you plan to sell",
        paragraphs: [
          "The most common mistake collectors make is grading with their heart. A card you pulled yourself always feels Near Mint. Buyers grade with their eyes, and an overgraded card comes back as a return. When in doubt between two grades, take the lower one — pricing at LP and delivering NM makes buyers happy; the reverse makes disputes.",
        ],
      },
      {
        heading: "How it works in CardArk",
        paragraphs: [
          "Every scanned card starts at Near Mint. Open any card and drag the condition slider — the price recalculates instantly from the Near Mint baseline, and your collection totals update with it. Add your own close-up condition photos to each card so the flaws are documented next to the value.",
        ],
      },
    ],
  },
  {
    slug: "organize-pokemon-card-collection",
    title: "How to Organize a Pokemon Card Collection Digitally",
    description:
      "Turn a shoebox of Pokemon cards into a searchable digital binder: auto-sorting by set, tracking duplicates, set checklists for missing cards, and watching prices over time.",
    category: "Collecting",
    readTime: "4 min read",
    intro:
      "A collection you can't search is just a pile. Digitizing your Pokemon cards means you always know what you own, what it's worth, and exactly which cards you're missing from each set. Here's a workflow that scales from 50 cards to 5,000.",
    sections: [
      {
        heading: "Step 1: Scan in batches by set",
        paragraphs: [
          "Stack and film cards from the same set together where you can. CardArk auto-files every identified card into a collection named after its set (e.g. 'Pokemon Crown Zenith'), so scanning set-by-set gives you perfectly organized collections with zero manual sorting.",
        ],
      },
      {
        heading: "Step 2: Review before confirming",
        paragraphs: [
          "After each scan, the Review screen shows your snapshot next to the AI's matched reference image with a green 'Verified' badge when the match is visually confirmed. Confirm All handles the obvious ones; give the flagged ones ten seconds each. Catching one misfile now beats hunting it later.",
        ],
      },
      {
        heading: "Step 3: Let duplicates stack, not clutter",
        paragraphs: [
          "Duplicate cards merge into a single entry with a quantity counter — three Pikachus show as one card ×3, and your collection value multiplies accordingly. Your binder stays readable no matter how many bulk commons you own.",
        ],
      },
      {
        heading: "Step 4: Use set checklists to find what's missing",
        paragraphs: [
          "Open any set collection and scan for its checklist — CardArk pulls the official full set list (with card images) and greys out every card you don't own yet. Flip the toggle to see owned-only or the full set with gaps. It turns 'I think I need a few more' into a literal shopping list.",
        ],
      },
      {
        heading: "Step 5: Watch values move",
        paragraphs: [
          "Every card keeps a price history, charted on its detail page. Pokemon prices move with set rotations, tournament results and reprints — a card you filed at $5 can quietly become a $30 card. Re-scan market prices any time for free, and export your whole collection to CSV for insurance or trade lists.",
        ],
      },
    ],
  },
  {
    slug: "pokemon-vs-sports-card-scanning",
    title: "Pokemon vs Sports Cards: What to Expect from AI Card Scanning",
    description:
      "CardArk works best with Pokemon cards. Sports cards are supported too — here's exactly what's different, why sports pricing data is thinner, and how to get the best results with sports cards.",
    category: "Scanning",
    readTime: "3 min read",
    intro:
      "CardArk is built Pokemon-first, and it shows: official full set databases, high-quality reference images, and deep market price coverage. Sports cards absolutely can be scanned and catalogued — but it's honest to set expectations up front.",
    sections: [
      {
        heading: "Why Pokemon cards work so well",
        list: [
          "Official set databases — every Pokemon set's complete card list and official artwork is available, so checklists and reference images are exact.",
          "Deep price coverage — Pokemon cards are heavily tracked by market data sources, so most cards get a real live Near Mint price.",
          "Consistent card design — names, set symbols and numbers sit in predictable places, which the vision AI reads reliably.",
        ],
      },
      {
        heading: "What's different with sports cards",
        paragraphs: [
          "Sports cards (and soccer cards especially) live in a far more fragmented data world: thousands of parallel variants, serial-numbered prints, and market sources that simply don't track every player in every set.",
        ],
        list: [
          "Pricing is often an estimate — when no live listing exists, CardArk shows a clearly-labelled AI estimate (amber, with a ~ prefix) instead of pretending to know.",
          "Image matches can be imperfect — the reference image pulled from the web may show a different parallel or border variant of the same card.",
          "Set checklists may be AI-drafted — full official lists don't exist for many sports sets, so checklists are generated and labelled as such.",
        ],
      },
      {
        heading: "How to get the best sports card results",
        list: [
          "Add context before re-scanning: '2023 Panini Prizm, Silver Prizm parallel, card #55' dramatically improves identification.",
          "Use the 'Possible matches' picker — when the AI isn't sure, it saves the candidates so you can confirm the right one in one click.",
          "Upload your own reference image for cards where no clean web match exists.",
          "Treat AI estimates as ballparks — check recent sold listings yourself before selling anything significant.",
        ],
      },
      {
        heading: "The bottom line",
        paragraphs: [
          "Use CardArk for sports cards as a cataloguing and organization tool first, and a pricing tool second. For Pokemon (and Magic: The Gathering), it's both — accurate identification, official images and real last-sold prices out of the box.",
        ],
      },
    ],
  },
  {
    slug: "how-much-is-my-pokemon-card-worth",
    title: "How Much Is My Pokemon Card Worth? A Step-by-Step Valuation Guide",
    description:
      "Learn how to value any Pokemon card using real last-sold prices, condition, rarity and edition. A practical walkthrough of what actually determines a card's market value in 2026.",
    category: "Pricing",
    readTime: "6 min read",
    intro:
      "\"How much is my Pokemon card worth?\" is the first question every collector asks — and the honest answer is: exactly what someone recently paid for the same card, in the same condition. Sticker prices and old guide books lie. Here's how to find a card's real value in a few minutes.",
    faqs: [
      { q: "What makes a Pokemon card valuable?", a: "Four things stack up: rarity (secret rares, alt arts and promos are scarcer), condition (a Near Mint card can be worth 4x a Damaged one), edition (1st Edition and Shadowless print runs command huge premiums), and demand (chase Pokemon like Charizard, Umbreon and Pikachu sell for far more than equally-rare cards of unpopular Pokemon)." },
      { q: "Where can I find what my Pokemon card actually sold for?", a: "Use completed/sold listings on eBay rather than asking prices, or a tool like CardArk that pulls real last-sold market data automatically so you don't have to search each card by hand." },
      { q: "Is my old Base Set card worth a lot?", a: "Only if it's in strong condition and ideally 1st Edition or Shadowless. A played, unlimited-edition common from Base Set is usually worth a dollar or two — a graded 1st Edition holo can be worth thousands. Condition and edition decide everything." },
    ],
    sections: [
      {
        heading: "1. Identify the exact card first",
        paragraphs: [
          "Two cards that look identical can be worth wildly different amounts. Before you can price anything, pin down the exact printing.",
        ],
        list: [
          "Set name and symbol — the little icon near the card number tells you which set it's from.",
          "Card number — e.g. 4/102. The number after the slash is the set size.",
          "Edition markers — a '1st Edition' stamp or the absence of a set symbol shadow (Shadowless) hugely changes value.",
          "Rarity symbol — circle (common), diamond (uncommon), star (rare), or special markings for holos, ultra and secret rares.",
        ],
      },
      {
        heading: "2. Grade the condition honestly",
        paragraphs: [
          "Condition is the single biggest lever on a raw card's price. Market prices are quoted for Near Mint; every grade below that applies a discount. Check centering, corners, edges and surface under good light — and grade with your eyes, not your heart.",
        ],
      },
      {
        heading: "3. Use real last-sold prices, not asking prices",
        paragraphs: [
          "Anyone can list a card for $500; that doesn't mean it sells for $500. The only number that matters is what buyers actually paid recently. Look at completed sold listings and take the median of the last several sales in your card's condition — ignore the outliers at the top and bottom.",
        ],
      },
      {
        heading: "4. Factor in edition, language and grading",
        list: [
          "1st Edition and Shadowless cards can be worth many times their unlimited-edition equivalents.",
          "Japanese, and especially early Japanese, prints have their own separate market — don't price them against English sales.",
          "A professionally graded card (PSA/CGC/Beckett) in a high grade sells for a premium over the same raw card, because the grade removes the buyer's condition risk.",
        ],
      },
      {
        heading: "The fast way with CardArk",
        paragraphs: [
          "Instead of searching every card by hand, scan your stack once. CardArk identifies each card, then attaches its real last-sold Near Mint price and lets you slide the condition dial to see the value at your card's exact grade — with the sold-price trend so you know if it's climbing or cooling.",
        ],
      },
    ],
  },
  {
    slug: "how-to-tell-if-a-pokemon-card-is-rare",
    title: "How to Tell If a Pokemon Card Is Rare",
    description:
      "Read the rarity symbol, spot holos, reverse holos, secret rares, alt arts and promos, and understand 1st Edition and Shadowless markers — a plain-English guide to Pokemon card rarity.",
    category: "Collecting",
    readTime: "5 min read",
    intro:
      "Rarity is baked right into every Pokemon card if you know where to look. Once you can read the symbols and spot the special finishes, you'll know at a glance whether a card is bulk or a banger. Here's the full breakdown.",
    faqs: [
      { q: "What does the symbol at the bottom of a Pokemon card mean?", a: "It's the rarity symbol. A circle means common, a diamond means uncommon, and a star means rare. Additional or coloured stars, and 'H'/letter codes on modern cards, indicate higher rarities like ultra rare, secret rare or illustration rare." },
      { q: "Are reverse holo cards rare?", a: "Reverse holos (where the card body is foil but the artwork isn't) are more of a collectible finish than a true rarity tier — most commons and uncommons have a reverse-holo version. They're nice to own but usually not high-value unless the card itself is desirable." },
      { q: "How do I know if a card is 1st Edition?", a: "Look for a black '1st Edition' stamp, usually on the left side below the artwork. On very early sets, the absence of a drop-shadow to the right of the artwork box ('Shadowless') is also a scarce, valuable early print." },
    ],
    sections: [
      {
        heading: "Step 1: Read the rarity symbol",
        paragraphs: [
          "Look at the bottom of the card, usually near the card number. That small symbol is the game's official rarity marker.",
        ],
        list: [
          "● Circle — Common.",
          "◆ Diamond — Uncommon.",
          "★ Star — Rare.",
          "Two or three stars, coloured stars or special letters — ultra rare, secret rare or illustration rare (the modern chase tiers).",
        ],
      },
      {
        heading: "Step 2: Check the finish",
        list: [
          "Holo rare — the artwork window is shiny/foil while the rest of the card is matte.",
          "Reverse holo — the card body is foil but the artwork is not; a collectible finish more than a rarity jump.",
          "Full art / alt art — the artwork extends across the whole card; these are highly sought after.",
          "Gold / rainbow / secret rare — card numbers that exceed the set total (e.g. 201/200) mark secret rares.",
        ],
      },
      {
        heading: "Step 3: Look for edition and print markers",
        paragraphs: [
          "On older cards, edition markers are where the real money hides. A '1st Edition' stamp and 'Shadowless' printing (no drop shadow beside the art box) are scarce early runs that command large premiums over standard unlimited prints.",
        ],
      },
      {
        heading: "Step 4: Promos and special sets",
        paragraphs: [
          "Black Star promos, tournament prizes, staff cards and sealed-product exclusives sit outside the normal rarity system and can be very valuable regardless of their symbol. When in doubt, the set symbol and card number tell you which release it came from.",
        ],
      },
      {
        heading: "Let the AI read it for you",
        paragraphs: [
          "CardArk's vision AI reads the set, number and rarity straight off the artwork when you scan, and files the card with its rarity and a real market price — so you can spot the valuable cards in a bulk pile without squinting at symbols one by one.",
        ],
      },
    ],
  },
  {
    slug: "how-to-spot-fake-pokemon-cards",
    title: "How to Spot a Fake Pokemon Card",
    description:
      "Proven tests to tell a genuine Pokemon card from a counterfeit: the light test, texture and font checks, colour and back-print tells, and the rip test as a last resort.",
    category: "Collecting",
    readTime: "5 min read",
    intro:
      "Counterfeit Pokemon cards have flooded marketplaces, and some are convincing at a glance. The good news: fakes almost always fail simple physical tests. Here are the checks that catch the vast majority of them.",
    faqs: [
      { q: "What is the easiest way to spot a fake Pokemon card?", a: "The light test. Hold the card up to a bright light — genuine cards have a thin black cardboard layer in the middle that blocks most light, so they look opaque. Most fakes let far more light through because they use cheaper stock without that layer." },
      { q: "Do fake Pokemon cards feel different?", a: "Usually yes. Genuine modern cards have a subtle textured, slightly glossy front and crisp printing. Fakes often feel too smooth, too glossy or oddly slippery, with slightly blurry text and off colours when compared side by side with a card you know is real." },
      { q: "Are all cheap or bulk cards fake?", a: "No — most bulk commons are genuine and simply low-value. Fakes are more common among expensive chase cards, sealed-looking 'vintage' lots and suspiciously cheap graded slabs. Scrutinise the expensive stuff, not the dollar commons." },
    ],
    sections: [
      {
        heading: "The light test (start here)",
        paragraphs: [
          "Genuine Pokemon cards are made of three layers, with a thin black layer sandwiched in the middle. Hold the card up to a strong light: a real card stays mostly opaque, while many fakes glow through because they skip that black core. Compare against a card you know is authentic.",
        ],
      },
      {
        heading: "Texture and print quality",
        list: [
          "Real modern cards have a fine texture and sharp, clean text; fakes often look slightly fuzzy or pixelated under magnification.",
          "Check the font weight and spacing against a known-genuine card of the same era — counterfeiters frequently get the typography subtly wrong.",
          "Foil on fakes can look flat, overly mirror-like, or applied to the wrong areas.",
        ],
      },
      {
        heading: "Colour and the card back",
        paragraphs: [
          "Fakes frequently have off colours — a bluer or greener back, or washed-out artwork. The blue of the card back and the swirl pattern are common tells. Any spelling mistakes, wrong energy symbols or incorrect HP for a known card are instant red flags.",
        ],
      },
      {
        heading: "Weight, cut and edges",
        list: [
          "Genuine cards have consistent weight and a clean, even cut; fakes can be too thin, too thick, or unevenly trimmed.",
          "Look at the edges — a real card's black core is sometimes visible as a thin dark line along the edge.",
        ],
      },
      {
        heading: "The rip test (last resort)",
        paragraphs: [
          "If a low-value card is still ambiguous and you're willing to destroy it, tearing a corner reveals the black core in a genuine card. Never do this to a card that might be valuable — use the non-destructive tests first, and when buying expensive cards, favour professionally graded slabs from reputable sellers.",
        ],
      },
    ],
  },
  {
    slug: "psa-vs-cgc-vs-beckett-grading",
    title: "PSA vs CGC vs Beckett: Which Grading Company Should You Use?",
    description:
      "A clear comparison of the three main card grading companies — PSA, CGC and Beckett (BGS) — covering resale value, turnaround, cost, subgrades and which to pick for Pokemon cards.",
    category: "Grading",
    readTime: "6 min read",
    intro:
      "Getting a valuable card graded protects it, authenticates it, and usually raises its resale value. But PSA, CGC and Beckett aren't interchangeable — the label on the slab affects what buyers will pay. Here's how the big three compare.",
    faqs: [
      { q: "Which grading company is best for Pokemon cards?", a: "For most Pokemon, PSA slabs currently command the strongest resale demand and liquidity, which is why they're the default choice for many sellers. CGC is a popular, often faster and cheaper alternative with strong Pokemon reputation, and Beckett (BGS) is prized for its subgrades and the coveted Black Label 10." },
      { q: "Does grading actually increase a card's value?", a: "For desirable cards in strong condition, yes — a high grade removes the buyer's condition risk and authenticates the card, so graded copies typically sell for a premium over the same raw card. For low-value or heavily played cards, grading fees usually cost more than the value they add." },
      { q: "What grade is worth getting?", a: "Generally a card needs to come back a 9 or 10 (Gem Mint) to justify the fee and earn a meaningful premium. Cards with visible wear, off-centering or surface scratches often grade 7-8 and may not be worth grading unless the card is scarce." },
    ],
    sections: [
      {
        heading: "PSA — the market default",
        paragraphs: [
          "PSA (Professional Sports Authenticator) grades on a 1-10 scale with a single overall grade. Its slabs generally have the deepest buyer demand and easiest resale for Pokemon, which is why a PSA 10 is the benchmark most collectors chase.",
        ],
      },
      {
        heading: "CGC — fast-growing and value-friendly",
        paragraphs: [
          "CGC Cards built a strong reputation in the trading-card space and is popular for Pokemon. It's often more affordable and faster than PSA, uses a 1-10 scale (with a Pristine 10 above the standard Gem Mint 10), and its slabs are well regarded — though resale premiums can trail PSA for some cards.",
        ],
      },
      {
        heading: "Beckett (BGS) — subgrades and the Black Label",
        paragraphs: [
          "Beckett is known for its four subgrades (centering, corners, edges, surface) printed on the label, giving buyers a detailed breakdown. A BGS 10 with four perfect subgrades — the famous Black Label — is one of the most prestigious (and hardest to earn) grades in the hobby.",
        ],
      },
      {
        heading: "How to choose",
        list: [
          "Selling for maximum liquidity and mainstream demand → PSA.",
          "Balancing cost, speed and quality → CGC.",
          "Want a detailed condition breakdown or chasing a perfect flagship grade → Beckett.",
          "Always check current turnaround times and fees before submitting — they change often.",
        ],
      },
      {
        heading: "Decide what's worth submitting first",
        paragraphs: [
          "Before you pay any grading fee, know the card's raw and graded values. Scan your collection with CardArk to see each card's last-sold raw and graded prices side by side — so you only send off the cards where a high grade clearly pays for itself.",
        ],
      },
    ],
  },
  {
    slug: "how-to-sell-pokemon-card-collection",
    title: "How to Sell Your Pokemon Card Collection for the Best Price",
    description:
      "From cataloguing and pricing to choosing between eBay, marketplaces and local buyers — a practical playbook for selling a Pokemon card collection without leaving money on the table.",
    category: "Selling",
    readTime: "7 min read",
    intro:
      "Selling a collection well is mostly preparation. The collectors who get the best prices know exactly what they have, what it's worth, and which cards to sell individually versus in bulk. Here's the playbook.",
    faqs: [
      { q: "Where is the best place to sell Pokemon cards?", a: "For valuable singles, eBay reaches the most buyers and its sold listings set the market price. Dedicated card marketplaces and Facebook/Discord groups can mean lower fees. Local card shops offer instant cash but pay wholesale (often 50-70% of value). Bulk commons are usually sold by weight or in lots." },
      { q: "Should I sell cards individually or as a lot?", a: "Sell your valuable chase cards individually to capture full market value, and combine low-value commons and bulk into lots — listing dollar commons one by one isn't worth the time and fees. A good rule: anything worth more than roughly $5-10 is usually better sold as a single." },
      { q: "Do I need to grade cards before selling?", a: "Only your best cards. Grading adds value and cost, so it pays off for high-value cards in strong condition but not for common or played cards. Sell those raw." },
    ],
    sections: [
      {
        heading: "1. Catalogue everything first",
        paragraphs: [
          "You can't price what you can't see. Get a full inventory of what you own, ideally with each card's set, number, condition and current market value. This is where most sellers lose money — they sell blind and let a valuable card go for bulk prices.",
        ],
      },
      {
        heading: "2. Price against real sold listings",
        paragraphs: [
          "Use recent completed sales, not asking prices, to set your numbers. Price the card at its actual condition — overpricing kills listings, and underpricing leaves money behind. Watch the trend too: a card that's been climbing may be worth holding a little longer.",
        ],
      },
      {
        heading: "3. Split singles from bulk",
        list: [
          "Chase cards, holos, rares and anything above ~$5-10 → sell as individual listings.",
          "Commons, energy and low-value uncommons → bundle into bulk lots or sell by weight.",
          "Complete or near-complete sets → sometimes worth more sold together as a set.",
        ],
      },
      {
        heading: "4. Choose the right venue",
        list: [
          "eBay — biggest audience and price discovery, but fees apply; best for valuable singles.",
          "Marketplaces / Discord / Facebook groups — lower fees, more effort, good for enthusiast buyers.",
          "Local card shop — instant cash but wholesale pricing; convenient for bulk.",
        ],
      },
      {
        heading: "5. Photograph and describe honestly",
        paragraphs: [
          "Clear, well-lit photos of the actual card (front and back) and an honest condition description win buyer trust and reduce returns. Overgrading your own cards is the fastest route to disputes.",
        ],
      },
      {
        heading: "Prep your sale with CardArk",
        paragraphs: [
          "Scan your whole collection, and CardArk gives you a priced, exportable inventory with last-sold values and condition applied per card. Mark cards as Under Offer or Sold, and the built-in Sales Ledger tracks what each card actually sold for versus market — so you know exactly how your sale performed.",
        ],
      },
    ],
  },
  {
    slug: "most-valuable-pokemon-cards",
    title: "The Most Valuable Pokemon Cards (and How to Know If You Have One)",
    description:
      "The categories of Pokemon cards that command the highest prices — vintage 1st Edition holos, promos, errors and modern chase alt arts — plus how to check if a card in your binder is a hidden gem.",
    category: "Collecting",
    readTime: "6 min read",
    intro:
      "Everyone hopes there's a small fortune hiding in their old binder. Sometimes there is. While record-breaking cards make headlines, the real point is knowing which categories carry value — and how to check your own cards against the market.",
    faqs: [
      { q: "What is the most valuable Pokemon card?", a: "The highest prices go to ultra-scarce items like the Pikachu Illustrator promo and pristine graded 1st Edition Base Set Charizards, which have sold for six and seven figures. These are exceptional — but the categories they belong to (early 1st Edition holos, tournament promos, error cards) are where value concentrates." },
      { q: "How do I know if my Pokemon card is valuable?", a: "Check three things: is it a holo/rare/alt art, is it an early edition (1st Edition or Shadowless), and what have recent copies in its condition actually sold for? A tool like CardArk identifies the card and attaches a real last-sold price so you don't have to guess." },
      { q: "Are modern Pokemon cards worth anything?", a: "Yes — modern chase cards like special-illustration and alternate-art rares (think sought-after Umbreon, Charizard and Pikachu alt arts) can be worth hundreds in high grades. Value isn't only a vintage thing." },
    ],
    sections: [
      {
        heading: "Vintage 1st Edition & Shadowless holos",
        paragraphs: [
          "The classic value category: early WOTC-era holos, especially 1st Edition and Shadowless Base Set. Condition is everything here — a high-grade 1st Edition Charizard is a hobby grail, while a played unlimited copy is worth a tiny fraction.",
        ],
      },
      {
        heading: "Promos, prizes and staff cards",
        paragraphs: [
          "Tournament prize cards, staff cards, and limited promos printed in tiny quantities routinely command high prices because supply is minuscule. Illustrator-style and trophy cards are the extreme end of this category.",
        ],
      },
      {
        heading: "Error and misprint cards",
        paragraphs: [
          "Genuine printing errors — miscuts, ink errors, missing colours — can carry big premiums with collectors, provided the error is authentic and well documented. Beware deliberately damaged cards passed off as errors.",
        ],
      },
      {
        heading: "Modern chase alt arts and secret rares",
        paragraphs: [
          "You don't need vintage to have value. Alternate-art and special-illustration rares of popular Pokemon from recent sets — and gold/rainbow secret rares — can be worth serious money, especially graded gem mint.",
        ],
      },
      {
        heading: "How to check your own binder",
        paragraphs: [
          "Rather than eyeballing it, scan your collection. CardArk identifies every card and attaches its real last-sold price, sorting your binder by value so any hidden gem floats straight to the top — and flags the sold-price trend so you know what's heating up.",
        ],
      },
    ],
  },
  {
    slug: "how-to-store-and-protect-pokemon-cards",
    title: "How to Store and Protect Your Pokemon Cards",
    description:
      "Sleeves, toploaders, binders and box storage explained — how to protect Pokemon cards from wear, sunlight and humidity, and keep valuable cards in the best possible condition.",
    category: "Collecting",
    readTime: "5 min read",
    intro:
      "Condition is value. A card kept pack-fresh can be worth several times the same card that's been shuffled loose in a drawer. Protecting your cards properly is the cheapest way to preserve — and grow — what your collection is worth.",
    faqs: [
      { q: "What is the best way to store valuable Pokemon cards?", a: "Double-sleeve the card (a penny sleeve inside a snug perfect-fit sleeve), then place it in a toploader or one-touch magnetic holder for rigid protection. Store upright, away from direct sunlight, heat and humidity. For your very best cards, professional grading in a sealed slab is the ultimate protection." },
      { q: "Do sleeves and toploaders really matter?", a: "Absolutely. Most condition damage — edge whitening, corner dings, surface scratches — happens from handling and loose storage. A few cents of sleeves and toploaders prevents damage that can knock a card down one or more grades." },
      { q: "How do I stop my cards from fading or warping?", a: "Keep them out of direct sunlight (UV fades ink), avoid high heat and humidity (which warps cardstock), and store cards upright rather than in tall stacks that press and bend the cards at the bottom." },
    ],
    sections: [
      {
        heading: "The protection ladder",
        list: [
          "Penny sleeve — the basic first layer for every card worth keeping.",
          "Perfect-fit sleeve + penny sleeve (double-sleeving) — snug protection for better cards.",
          "Toploader or one-touch holder — rigid protection that stops bending and corner damage.",
          "Graded slab — sealed, authenticated, tamper-evident; the ultimate for high-value cards.",
        ],
      },
      {
        heading: "Binders for the bulk of your collection",
        paragraphs: [
          "Side-loading binder pages protect better than top-loading ones (cards can't slip out). Use acid-free, PVC-free pages — cheap PVC pages can damage cards over time. Binders are ideal for sets and mid-value cards you want to browse.",
        ],
      },
      {
        heading: "Environment matters",
        list: [
          "Keep cards out of direct sunlight — UV fades artwork permanently.",
          "Avoid heat and humidity, which warp and stick cards together; a stable room temperature is ideal.",
          "Store cards upright, not in heavy stacks that press the bottom cards.",
        ],
      },
      {
        heading: "Keep a digital backup of your collection",
        paragraphs: [
          "Physical protection guards the cards; a digital record guards you. Scanning your collection with CardArk gives you a dated, priced inventory with photos of each card — invaluable for insurance, proving condition, or simply knowing what you own if anything is ever lost, stolen or damaged.",
        ],
      },
    ],
  },
  {
    slug: "is-it-worth-grading-pokemon-cards",
    title: "Is It Worth Grading Your Pokemon Cards? When Grading Pays Off",
    description:
      "How to decide whether to grade a Pokemon card: the maths of grading fees vs. graded premiums, which cards are worth submitting, and how to estimate the grade before you pay.",
    category: "Grading",
    readTime: "5 min read",
    intro:
      "Grading can multiply a card's value — or cost you more than the card is worth. The difference comes down to a simple calculation most collectors skip. Here's how to decide, card by card.",
    faqs: [
      { q: "Is it worth grading Pokemon cards?", a: "It's worth grading when the expected graded value minus the grading fee is clearly higher than the raw value — which is generally true for desirable cards in strong (likely 9-10) condition, and false for common or played cards where fees exceed any premium." },
      { q: "How much does it cost to grade a Pokemon card?", a: "Fees vary by company and service level (turnaround speed) and change regularly, so always check current pricing before submitting. Factor in shipping and insurance both ways — the true cost per card is higher than the headline fee." },
      { q: "How do I know what grade my card will get?", a: "Inspect centering, corners, edges and surface under bright light. Cards with even centering, sharp corners, clean edges and a flawless surface are candidates for a 9-10; any visible wear, whitening or off-centering usually caps the grade lower and may make grading uneconomical." },
    ],
    sections: [
      {
        heading: "The core calculation",
        paragraphs: [
          "Grading pays off when: (graded value at the expected grade) − (grading fee + shipping) > (current raw value). If that inequality isn't clearly true, keep the card raw. Run it honestly using real sold prices for both the raw card and the graded grade you realistically expect.",
        ],
      },
      {
        heading: "Good grading candidates",
        list: [
          "Desirable cards (chase Pokemon, alt arts, vintage holos) where graded premiums are large.",
          "Cards that look gem-mint: perfect centering, sharp corners, clean edges, flawless surface.",
          "Cards where a graded copy sells for meaningfully more than the raw copy.",
        ],
      },
      {
        heading: "When to skip grading",
        list: [
          "Common or low-value cards — fees will exceed any premium.",
          "Cards with visible wear that will likely grade 7 or below.",
          "Cards you plan to keep for enjoyment rather than resale (though slabs do protect them).",
        ],
      },
      {
        heading: "Estimate the grade before you pay",
        paragraphs: [
          "Pre-grade every candidate yourself under good light: centering first (the most common grade-killer), then corners, edges and surface. Being realistic here saves you from paying to grade cards that come back lower than hoped.",
        ],
      },
      {
        heading: "Compare raw vs graded in CardArk",
        paragraphs: [
          "CardArk shows each card's last-sold raw and graded prices side by side. That's exactly the data the grading calculation needs — so you can spot at a glance which cards clear the bar and which are better sold raw.",
        ],
      },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
